import { useEffect, useRef, useState } from 'react';

import { getPersistedObject } from '../utils/localStorage';

export type UseJobSseStatus = 'idle' | 'connecting' | 'open' | 'done' | 'error';

export type UseJobSseOptions<T> = {
  /** Détermine si le job a atteint un état terminal (déconnexion attendue après). */
  isTerminal: (job: T) => boolean;
  /** Callback appelé une fois un événement terminal reçu. */
  onTerminal?: (job: T) => void;
  /**
   * Récupère l'état initial via REST avant que la SSE ne pousse quoi que ce soit.
   * Utile quand l'état du job est persisté côté serveur (OCR) ; à omettre quand il n'y a
   * pas de persistance.
   */
  fallbackFetch?: () => Promise<T | null>;
  /**
   * Re-poll périodique de l'état via `fallbackFetch` tant que le job n'est pas terminal.
   * À activer pour les jobs persistés dont le flux SSE peut rester muet (ex. Kafka down côté
   * serveur : le job progresse en base mais aucun event n'est relayé) — la page reste alors
   * synchronisée sur l'état réel. Sans effet si `fallbackFetch` n'est pas fourni.
   */
  poll?: { intervalMs: number };
};

export type UseJobSseReturn<T> = {
  job: T | null;
  status: UseJobSseStatus;
  error: Error | null;
};

/**
 * Hook qui ouvre un flux SSE et expose la progression d'un job.
 *
 * Implémenté via `fetch` + `ReadableStream` (et non `EventSource`) parce que :
 *   1. `EventSource` ne supporte pas les headers personnalisés, et l'auth du projet utilise
 *      un Bearer token dans le localStorage (pas de cookie de session).
 *   2. Cela permet de réutiliser la même stratégie d'auth qu'`AxiosClient`.
 *
 * La reconnexion auto d'`EventSource` n'est pas reproduite ici : la durée d'un job est bornée
 * (quelques minutes) et le serveur ferme la connexion ~5s après l'event terminal. Pour les
 * cas pathologiques (perte réseau), `poll` sert de watchdog et un reload de la page repeuple
 * l'état initial via `fallbackFetch`.
 *
 * `options` est lu via une ref : seul un changement d'`url` reconnecte le flux, une nouvelle
 * identité de callback à chaque rendu ne relance rien.
 */
export default function useJobSse<T>(
  url: string | null,
  options: UseJobSseOptions<T>
): UseJobSseReturn<T> {
  const [job, setJob] = useState<T | null>(null);
  const [status, setStatus] = useState<UseJobSseStatus>('idle');
  const [error, setError] = useState<Error | null>(null);

  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    if (!url) {
      setStatus('idle');
      return;
    }

    const ctrl = new AbortController();
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    // Horodatage du dernier event SSE reçu : sert de signal de vie au poll de secours (watchdog).
    let lastEventAt = 0;
    // Bascule terminale unique : qu'elle vienne de la SSE, du fallback initial ou du poll, on ne
    // déclenche `onTerminal` qu'une fois et on coupe tout le reste.
    let finished = false;

    function stopPolling() {
      if (pollTimer !== null) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    }

    function finish(value: T) {
      if (finished) return;
      finished = true;
      setJob(value);
      setStatus('done');
      stopPolling();
      optionsRef.current.onTerminal?.(value);
      ctrl.abort();
    }

    function parseBlock(block: string): T | null {
      const dataLines = block
        .split('\n')
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.slice(5).replace(/^\s+/, ''));
      if (dataLines.length === 0) return null;
      try {
        return JSON.parse(dataLines.join('\n')) as T;
      } catch (e) {
        console.error('[useJobSse] failed to parse SSE data block', e, block);
        return null;
      }
    }

    async function consume(streamUrl: string) {
      setStatus('connecting');
      setError(null);

      const { fallbackFetch, poll } = optionsRef.current;

      if (fallbackFetch) {
        try {
          const initial = await fallbackFetch();
          if (ctrl.signal.aborted) return;
          if (initial) {
            setJob(initial);
            if (optionsRef.current.isTerminal(initial)) {
              finish(initial);
              return;
            }
          }
        } catch {
          // Le fallback est best-effort : on continue avec la SSE.
        }
      }

      // Poll de secours (watchdog) : ne fetch QUE si la SSE est restée muette depuis au moins un
      // intervalle (ex. Kafka down côté serveur : le job progresse en base mais aucun event
      // n'arrive). Tant que la SSE pousse des events, `lastEventAt` reste frais et rien ne part.
      if (poll && fallbackFetch) {
        const { intervalMs } = poll;
        let polling = false;
        pollTimer = setInterval(async () => {
          if (polling || finished || ctrl.signal.aborted) return;
          if (Date.now() - lastEventAt < intervalMs) return;
          polling = true;
          try {
            const snap = await fallbackFetch();
            if (ctrl.signal.aborted || finished || !snap) return;
            setJob(snap);
            if (optionsRef.current.isTerminal(snap)) finish(snap);
          } catch {
            // best-effort : on retentera au prochain tick.
          } finally {
            polling = false;
          }
        }, intervalMs);
      }

      try {
        const token = getPersistedObject('access_token');
        const response = await fetch(streamUrl, {
          method: 'GET',
          headers: {
            Accept: 'text/event-stream',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          signal: ctrl.signal,
        });

        if (!response.ok) {
          throw new Error(
            `SSE request failed: ${response.status} ${response.statusText}`
          );
        }
        if (!response.body) {
          throw new Error('SSE response has no body');
        }

        setStatus('open');

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (!ctrl.signal.aborted) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          let sepIdx = buffer.indexOf('\n\n');
          while (sepIdx !== -1) {
            const block = buffer.slice(0, sepIdx);
            buffer = buffer.slice(sepIdx + 2);
            const parsed = parseBlock(block);
            if (parsed) {
              lastEventAt = Date.now();
              setJob(parsed);
              if (optionsRef.current.isTerminal(parsed)) {
                finish(parsed);
                return;
              }
            }
            sepIdx = buffer.indexOf('\n\n');
          }
        }
      } catch (e) {
        if ((e as Error & { name?: string })?.name === 'AbortError') return;
        setError(e as Error);
        setStatus('error');
      }
    }

    void consume(url);

    return () => {
      stopPolling();
      ctrl.abort();
    };
  }, [url]);

  return { job, status, error };
}
